import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import Navbar from '../components/landing/Navbar';
import Footer from '../components/landing/Footer';
import { useLanguage } from '../i18n/LanguageContext';

export default function Activation() {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-black" data-testid="activation-page">
      <Navbar />

      {/* Header */}
      <section className="pt-40 pb-20 md:pt-48 md:pb-24 border-b border-hb-gray-border" data-testid="activation-header">
        <div className="max-w-[1100px] mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}>
            <Link
              to="/"
              className="inline-flex items-center gap-2 font-body text-xs uppercase tracking-[0.25em] text-hb-gray hover:text-white transition-colors mb-10"
              data-testid="activation-back-link">
              <span>&lsaquo;</span> {t.act_back}
            </Link>
            <p className="font-body text-[10px] uppercase tracking-[0.4em] text-hb-green mb-6">{t.act_eyebrow}</p>
            <h1 className="font-heading font-normal text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-white mb-8">
              {t.act_h}<br />
              <em className="text-hb-green">{t.act_h_accent}</em>
            </h1>
            <p className="font-body text-base md:text-lg text-hb-gray-light leading-relaxed max-w-[720px]">
              {t.act_lead}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Before you start */}
      <section className="py-16 md:py-20 border-b border-hb-gray-border">
        <div className="max-w-[1100px] mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-12"
            data-testid="activation-before">
            <div className="md:col-span-4">
              <p className="font-body text-[10px] uppercase tracking-[0.4em] text-hb-gray">{t.act_before_label}</p>
            </div>
            <div className="md:col-span-8">
              <p className="font-body text-base text-hb-gray-light leading-relaxed">{t.act_before_body}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Steps */}
      <section className="py-20 md:py-28">
        <div className="max-w-[1100px] mx-auto px-6 md:px-8">
          <div className="space-y-24 md:space-y-32">
            {t.act_steps.map((step, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start"
                data-testid={`activation-step-${i}`}>
                <div className="md:col-span-6">
                  <div className="flex items-baseline gap-5 mb-6">
                    <span className="font-heading italic text-5xl md:text-6xl text-hb-green leading-none">{step.num}</span>
                    <span className="h-px flex-1 bg-hb-gray-border" />
                  </div>
                  <h2 className="font-heading font-normal text-3xl md:text-4xl leading-tight text-white mb-6">{step.title}</h2>
                  {step.body.split('\n\n').map((para, k) => (
                    <p key={k} className="font-body text-base text-hb-gray-light leading-relaxed mb-4 last:mb-0">
                      {para}
                    </p>
                  ))}
                </div>
                <div className="md:col-span-6">
                  <div className="border border-hb-gray-border bg-hb-dark p-3 md:p-4">
                    <img
                      src={step.image}
                      alt=""
                      className="w-full h-auto object-contain max-h-[560px] mx-auto block"
                    />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning callout */}
      <section className="py-16 md:py-20 border-t border-hb-gray-border">
        <div className="max-w-[1100px] mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="border border-hb-green/30 bg-hb-green/[0.04] p-8 md:p-10"
            data-testid="activation-warning">
            <p className="font-body text-[10px] uppercase tracking-[0.4em] text-hb-green mb-4">{t.act_warning_label}</p>
            <p className="font-body text-base text-hb-gray-light leading-relaxed">{t.act_warning_body}</p>
          </motion.div>
        </div>
      </section>

      {/* Last step */}
      <section className="py-20 md:py-28 border-t border-hb-gray-border">
        <div className="max-w-[1100px] mx-auto px-6 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            data-testid="activation-last">
            <p className="font-body text-[10px] uppercase tracking-[0.4em] text-hb-gray mb-6">{t.act_last_label}</p>
            <h2 className="font-heading font-normal text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight text-white mb-8">
              {t.act_last_h} <em className="text-hb-green">{t.act_last_h_accent}</em>
            </h2>
            <p className="font-body text-base md:text-lg text-hb-gray-light leading-relaxed max-w-[700px] mb-10">
              {t.act_last_body}
            </p>
            <p className="font-body text-sm text-hb-gray leading-relaxed">
              {t.act_help_pre}{' '}
              <a href={`mailto:${t.foot_email}`} className="text-white hover:text-hb-green underline underline-offset-4 transition-colors" data-testid="activation-help-email">{t.foot_email}</a>.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
